import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Building,
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  DollarSign,
  Download,
  Edit,
  Eye,
  Factory,
  FileDown,
  FileText,
  Filter,
  History,
  Image as ImageIcon,
  Layers,
  MessageSquare,
  Package,
  Plus,
  Rocket,
  Search,
  Settings,
  Shield,
  Target,
  Trash2,
  TrendingUp,
  Upload,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AuthImage } from "./AuthImage";
import { STATUS_CONFIG, PRIORITY_CONFIG, TYPE_CONFIG } from "./constants";
import { StatusChangeModal } from "./StatusChangeModal";
import { ParecerModal } from "./ParecerModal";
import { UploadModal } from "./UploadModal";
import { RevisionModal } from "./RevisionModal";
import { ComponentModal } from "./ComponentModal";
import { AssigneeModal } from "./AssigneeModal";
import { EditSDPModal } from "./EditSDPModal";
import { ImageGalleryModal } from "./ImageGalleryModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SDPDetailView({ sdp, onClose, onUpdate, token, canEdit, isAdmin, linhasProduto, users }) {
  const [activeTab, setActiveTab] = useState("geral");
  const [loading, setLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showParecerModal, setShowParecerModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageGalleryModal, setShowImageGalleryModal] = useState(false);
  const [editField, setEditField] = useState(null); // 'prioridade' or 'justificativa'
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingPdfCliente, setExportingPdfCliente] = useState(false);

  const statusCfg = STATUS_CONFIG[sdp.status] || STATUS_CONFIG.solicitacao_aberta;
  const priorityCfg = PRIORITY_CONFIG[sdp.prioridade] || PRIORITY_CONFIG.media;
  const headers = { Authorization: `Bearer ${token}` };

  const handleStatusChange = async (novoStatus, comentario) => {
    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/sdp/${sdp.id}/status`, {
        novo_status: novoStatus,
        comentario
      }, { headers });
      toast.success("Status alterado com sucesso!");
      onUpdate();
      setShowStatusModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar status");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await axios.get(`${API_URL}/api/sdp/${sdp.id}/files/${file.id}/download`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Erro ao baixar arquivo");
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!confirm("Tem certeza que deseja excluir este arquivo?")) return;
    try {
      await axios.delete(`${API_URL}/api/sdp/${sdp.id}/files/${fileId}`, { headers });
      toast.success("Arquivo excluído");
      onUpdate();
    } catch (error) {
      toast.error("Erro ao excluir arquivo");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sdp/${sdp.id}/export`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sdp.codigo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      const response = await axios.get(`${API_URL}/api/sdp/${sdp.id}/export-pdf`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sdp.codigo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportPdfCliente = async () => {
    try {
      setExportingPdfCliente(true);
      const response = await axios.get(`${API_URL}/api/sdp/${sdp.id}/export-pdf-cliente`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sdp.codigo}_cliente.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF para cliente exportado!");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    } finally {
      setExportingPdfCliente(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-10">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white rounded-t-lg z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={onClose}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xl font-bold text-blue-600">{sdp.codigo}</span>
                  <Badge className={priorityCfg.color}>{priorityCfg.label}</Badge>
                  <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                </div>
                <h2 className="text-lg font-medium text-slate-800 mt-1">{sdp.nome_projeto}</h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={exportingPdf}>
                <FileDown className="w-4 h-4 mr-2" />
                {exportingPdf ? "Gerando..." : "PDF"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPdfCliente} disabled={exportingPdfCliente} title="PDF para compartilhar com cliente">
                <Users className="w-4 h-4 mr-2" />
                {exportingPdfCliente ? "Gerando..." : "PDF Cliente"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Excel
              </Button>
              {canEdit && (
                <Button size="sm" onClick={() => setShowStatusModal(true)}>
                  Alterar Status
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="p-6">
          <TabsList className="grid grid-cols-6 w-full mb-6">
            <TabsTrigger value="geral">Dados Gerais</TabsTrigger>
            <TabsTrigger value="imagens">Imagens</TabsTrigger>
            <TabsTrigger value="arquivos">Arquivos ({sdp.arquivos?.length || 0})</TabsTrigger>
            <TabsTrigger value="revisoes">Revisões ({sdp.revisoes?.length || 0})</TabsTrigger>
            <TabsTrigger value="componentes">Lista Técnica ({sdp.componentes?.length || 0})</TabsTrigger>
            <TabsTrigger value="historico">Histórico</TabsTrigger>
          </TabsList>

          <TabsContent value="geral" className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Informações do Projeto</CardTitle>
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => { setEditField('prioridade'); setShowEditModal(true); }}>
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Cliente:</span>
                    <span className="font-medium">{sdp.cliente || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Linha de Produto:</span>
                    <span className="font-medium">{sdp.linha_produto_nome || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tipo:</span>
                    <span className="font-medium">{TYPE_CONFIG[sdp.tipo]?.label}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Prioridade:</span>
                    <Badge className={priorityCfg.color}>
                      {priorityCfg.label}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Prazo Desejado:</span>
                    <span className="font-medium">{sdp.prazo_desejado || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Revisão Atual:</span>
                    <span className="font-medium">{sdp.revisao_atual || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Responsáveis</CardTitle>
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => setShowAssigneeModal(true)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Criado por:</span>
                    <span className="font-medium">{sdp.created_by_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Data Criação:</span>
                    <span className="font-medium">{new Date(sdp.created_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Atribuído a:</span>
                    <span className="font-medium">
                      {sdp.assignee_name ? (
                        <Badge className={
                          sdp.assignee_type === 'admin' ? "bg-purple-100 text-purple-700" :
                          sdp.assignee_type === 'engenharia' ? "bg-blue-100 text-blue-700" :
                          "bg-slate-100 text-slate-700"
                        }>
                          {sdp.assignee_name}
                        </Badge>
                      ) : "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Responsável Técnico:</span>
                    <span className="font-medium">{sdp.responsavel_tecnico_nome || "-"}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Justificativa / Detalhamento</CardTitle>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => { setEditField('justificativa'); setShowEditModal(true); }}>
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <pre className="text-slate-700 whitespace-pre-wrap text-sm font-sans">{sdp.justificativa}</pre>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Parecer Técnico</CardTitle>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => setShowParecerModal(true)}>
                    {sdp.parecer_tecnico ? "Editar Parecer" : "Adicionar Parecer"}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {sdp.parecer_tecnico ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge className={
                        sdp.parecer_tecnico.viabilidade === "viavel" ? "bg-green-100 text-green-700" :
                        sdp.parecer_tecnico.viabilidade === "inviavel" ? "bg-red-100 text-red-700" :
                        "bg-yellow-100 text-yellow-700"
                      }>
                        {sdp.parecer_tecnico.viabilidade === "viavel" ? "Viável" :
                         sdp.parecer_tecnico.viabilidade === "inviavel" ? "Inviável" : "Requer Análise"}
                      </Badge>
                      {sdp.parecer_tecnico.estimativa_horas && (
                        <span className="text-sm text-slate-500">
                          Estimativa: {sdp.parecer_tecnico.estimativa_horas}h
                        </span>
                      )}
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap">{sdp.parecer_tecnico.parecer}</p>
                    {sdp.parecer_tecnico.cronograma && (
                      <div>
                        <span className="text-sm font-medium text-slate-500">Cronograma:</span>
                        <p className="text-slate-700">{sdp.parecer_tecnico.cronograma}</p>
                      </div>
                    )}
                    <p className="text-xs text-slate-400">
                      Por {sdp.parecer_tecnico.autor_nome} em {new Date(sdp.parecer_tecnico.data).toLocaleString('pt-BR')}
                    </p>
                  </div>
                ) : (
                  <p className="text-slate-500 italic">Nenhum parecer técnico registrado.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="imagens" className="space-y-6">
            {/* Cover Image */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Imagem de Capa
                </CardTitle>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => setShowImageGalleryModal(true)}>
                    <Edit className="w-4 h-4 mr-1" />
                    Gerenciar Imagens
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {sdp.cover_image ? (
                  <div className="rounded-lg overflow-hidden border border-slate-200">
                    <AuthImage
                      src={`${API_URL}/api/sdp/${sdp.id}/cover-image`}
                      alt="Capa do produto"
                      className="w-full max-h-80 object-contain bg-slate-50"
                      token={token}
                    />
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>Nenhuma imagem de capa definida</p>
                    {canEdit && (
                      <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowImageGalleryModal(true)}>
                        <Upload className="w-4 h-4 mr-2" />
                        Adicionar Imagem
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gallery Images */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Galeria de Imagens do Produto
                  <span className="text-sm font-normal text-slate-500">
                    ({sdp.gallery_images?.length || 0}/5)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sdp.gallery_images && sdp.gallery_images.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {sdp.gallery_images.map((img) => (
                      <div key={img.id} className="rounded-lg overflow-hidden border border-slate-200 group">
                        <div className="relative">
                          <AuthImage
                            src={`${API_URL}/api/sdp/${sdp.id}/gallery/${img.id}`}
                            alt={img.label}
                            className="w-full h-40 object-cover"
                            token={token}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="p-2 bg-slate-50">
                          <p className="font-medium text-sm text-slate-700">{img.label}</p>
                          <p className="text-xs text-slate-500">{img.uploaded_by_name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                    <Layers className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>Nenhuma imagem na galeria</p>
                    {canEdit && (
                      <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowImageGalleryModal(true)}>
                        <Upload className="w-4 h-4 mr-2" />
                        Adicionar Imagens
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="arquivos">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Arquivos do Projeto</CardTitle>
                {canEdit && (
                  <Button size="sm" onClick={() => setShowUploadModal(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {sdp.arquivos && sdp.arquivos.length > 0 ? (
                  <div className="space-y-2">
                    {sdp.arquivos.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="font-medium text-sm">{file.filename}</p>
                            <p className="text-xs text-slate-500">
                              {file.categoria} • {(file.file_size / 1024).toFixed(1)} KB • {file.uploaded_by_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(file)}>
                            <Download className="w-4 h-4" />
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteFile(file.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Nenhum arquivo anexado.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="revisoes">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Controle de Revisões</CardTitle>
                {canEdit && (
                  <Button size="sm" onClick={() => setShowRevisionModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Revisão
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {sdp.revisoes && sdp.revisoes.length > 0 ? (
                  <div className="space-y-3">
                    {sdp.revisoes.map((rev) => (
                      <div key={rev.id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl font-bold text-blue-600">Rev. {rev.numero}</span>
                            {rev.liberada_producao && (
                              <Badge className="bg-green-100 text-green-700">Liberada p/ Produção</Badge>
                            )}
                          </div>
                          <span className="text-sm text-slate-500">
                            {new Date(rev.created_at).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-slate-600">
                          <strong>Motivo:</strong> {rev.motivo_revisao}
                        </p>
                        {rev.descricao && (
                          <p className="mt-1 text-sm text-slate-500">{rev.descricao}</p>
                        )}
                        <p className="mt-2 text-xs text-slate-400">Por {rev.created_by_name}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Nenhuma revisão registrada.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="componentes">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Lista Técnica Preliminar</CardTitle>
                {canEdit && (
                  <Button size="sm" onClick={() => setShowComponentModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Item
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {sdp.componentes && sdp.componentes.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Código</th>
                          <th className="text-left py-2 px-3">Descrição</th>
                          <th className="text-center py-2 px-3">Qtd</th>
                          <th className="text-center py-2 px-3">Un</th>
                          <th className="text-center py-2 px-3">Existente?</th>
                          <th className="text-left py-2 px-3">Obs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sdp.componentes.map((comp) => (
                          <tr key={comp.id} className="border-b hover:bg-slate-50">
                            <td className="py-2 px-3 font-mono">{comp.codigo || "-"}</td>
                            <td className="py-2 px-3">{comp.descricao}</td>
                            <td className="py-2 px-3 text-center">{comp.quantidade}</td>
                            <td className="py-2 px-3 text-center">{comp.unidade}</td>
                            <td className="py-2 px-3 text-center">
                              {comp.item_existente ? (
                                <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500 mx-auto" />
                              )}
                            </td>
                            <td className="py-2 px-3 text-slate-500">{comp.observacoes || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Nenhum componente cadastrado.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="historico">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Histórico de Alterações
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sdp.historico && sdp.historico.length > 0 ? (
                  <div className="space-y-3">
                    {sdp.historico.map((h) => (
                      <div key={h.id} className="flex gap-4 p-3 border-l-2 border-blue-300 bg-slate-50 rounded-r">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{h.action}</Badge>
                            <span className="text-sm text-slate-500">por {h.user_name}</span>
                          </div>
                          {h.details && Object.keys(h.details).length > 0 && (
                            <p className="text-sm text-slate-600 mt-1">
                              {h.details.comentario || h.details.de ? `${h.details.de} → ${h.details.para}` : JSON.stringify(h.details)}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {new Date(h.timestamp).toLocaleString('pt-BR')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Nenhum histórico registrado.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <StatusChangeModal
        open={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onSubmit={handleStatusChange}
        currentStatus={sdp.status}
        loading={loading}
      />

      <ParecerModal
        open={showParecerModal}
        onClose={() => setShowParecerModal(false)}
        onSuccess={() => {
          setShowParecerModal(false);
          onUpdate();
        }}
        sdpId={sdp.id}
        token={token}
        existing={sdp.parecer_tecnico}
      />

      <UploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          setShowUploadModal(false);
          onUpdate();
        }}
        sdpId={sdp.id}
        token={token}
      />

      <RevisionModal
        open={showRevisionModal}
        onClose={() => setShowRevisionModal(false)}
        onSuccess={() => {
          setShowRevisionModal(false);
          onUpdate();
        }}
        sdpId={sdp.id}
        token={token}
      />

      <ComponentModal
        open={showComponentModal}
        onClose={() => setShowComponentModal(false)}
        onSuccess={() => {
          setShowComponentModal(false);
          onUpdate();
        }}
        sdpId={sdp.id}
        token={token}
      />

      <AssigneeModal
        open={showAssigneeModal}
        onClose={() => setShowAssigneeModal(false)}
        onSuccess={() => {
          setShowAssigneeModal(false);
          onUpdate();
        }}
        sdpId={sdp.id}
        token={token}
        users={users}
        currentAssigneeType={sdp.assignee_type}
        currentAssigneeId={sdp.assignee_id}
      />

      <EditSDPModal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setEditField(null); }}
        onSuccess={() => {
          setShowEditModal(false);
          setEditField(null);
          onUpdate();
        }}
        sdpId={sdp.id}
        token={token}
        field={editField}
        currentPrioridade={sdp.prioridade}
        currentJustificativa={sdp.justificativa}
      />

      <ImageGalleryModal
        open={showImageGalleryModal}
        onClose={() => setShowImageGalleryModal(false)}
        onSuccess={() => {
          setShowImageGalleryModal(false);
          onUpdate();
        }}
        sdpId={sdp.id}
        token={token}
        coverImage={sdp.cover_image}
        galleryImages={sdp.gallery_images || []}
      />
    </div>
  );
}

export { SDPDetailView };
